import { format } from 'date-fns';
import { ForwardedRef, forwardRef } from 'react';
import { useNavigate } from 'react-router';
import { FETCH_JOBS_WITH_TRANSACTIONS, FETCH_RECEIPT_LIST } from '~/actions';
import { Box, Column, DataGrid, Row, SortingArrow, Text, TSortValue } from '~/commonComponents';
import { useAppSelector, useLoading } from '~/hooks';
import { ArrowRightIcon } from '~/images/icons';
import { IJobsWithTransaction } from '~/models';
import { Colors } from '~/themes/colors';
import { getJobStatus } from '../../utils';

interface IPostingPaymentHistoryTableProps {
  sort: TSortValue;
  onSort: (field: string) => void;
  onScroll: () => void;
}

export const PostingPaymentHistoryTable = forwardRef<
  HTMLDivElement,
  IPostingPaymentHistoryTableProps
>(({ sort, onScroll, onSort }, ref: ForwardedRef<HTMLDivElement>) => {
  const navigate = useNavigate();

  const transactions: IJobsWithTransaction[] = useAppSelector(
    (state) => state.job.jobsWithTransaction,
  );
  const { isLoading } = useLoading([FETCH_RECEIPT_LIST, FETCH_JOBS_WITH_TRANSACTIONS]);

  return (
    <Column
      data-cy="shift-receipts-table"
      marginBottom={20}
      onScroll={onScroll}
      overflowX="auto"
      overflowY="auto"
      ref={ref}
      width="100%"
    >
      <DataGrid
        columns={[
          {
            key: 'office',
            header: 'Office',
            render: (item) => (
              <Text data-cy="office-name-column">{item?.offer_owner?.office_name}</Text>
            ),
          },
          {
            key: 'type',
            header: 'Type',
            width: '140px',
            render: (item) => (
              <Box
                alignSelf="flex-start"
                paddingVertical={2}
                paddingHorizontal={10}
                borderRadius={8}
                backgroundColor={
                  item.isOnsiteInterview
                    ? Colors.jobs_interview_background
                    : Colors.jobs_shift_background
                }
              >
                <Text color="neutral_500">{item.isOnsiteInterview ? 'INTERVIEW' : 'SHIFT'}</Text>
              </Box>
            ),
          },
          {
            key: 'professional',
            header: 'Professional',
            width: '220px',
            render: (item) => (
              <Text data-cy="professional-column">{item.offer_final_reciever || '-'}</Text>
            ),
          },
          {
            key: 'date',
            header: (
              <Row gap={8}>
                <Text bold color={Colors.primary_500} fontSize={18}>
                  Date
                </Text>
                <SortingArrow
                  name="jobDate"
                  sorting={sort}
                  toggleOnly
                  onChange={(val) => onSort(val?.field || '')}
                />
              </Row>
            ),
            width: '200px',
            render: (item) => (
              <Text data-cy="shift-date-column">
                {format(new Date(item.shift_date), 'EEE, MMM dd, yyyy')}
              </Text>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            width: '120px',
            render: (item) => (
              <Text
                bold
                color={getJobStatus(item.shift_status)?.color}
                data-cy="payment-status-column"
                fontSize={13}
                textTransform="uppercase"
              >
                {getJobStatus(item.shift_status)?.label || item.shift_status}
              </Text>
            ),
          },
          {
            key: 'transaction',
            header: 'Transaction',
            width: '110px',
            render: (item) => <Text>{item.no_transactions}</Text>,
          },
          {
            key: 'action',
            header: '',
            width: '50px',
            align: 'center',
            render: () => <ArrowRightIcon />,
          },
        ]}
        data={transactions}
        dataCy={{
          header: 'payment-history-table-header',
        }}
        keyExtractor={(item) => item.id}
        isLoading={isLoading}
        isStickyHeader
        onRowClick={(item) => navigate(`/dashboard/receipts/transactions/${item.id}`)}
      />
    </Column>
  );
});

PostingPaymentHistoryTable.displayName = 'PostingPaymentHistoryTable';
